import { CheckIcon, CircleAlertIcon, CircleIcon, MinusIcon, XIcon } from "lucide-react";
import { Spinner } from "../ui/spinner";
import { launchElapsed, type LaunchStage } from "./worktreeLaunchProgress";
const STAGE_ICON_CLASS_NAME = "size-4 shrink-0 stroke-[1.8]";

function StageIcon({ status }: { readonly status: LaunchStage["status"] }) {
  switch (status) {
    case "running":
      return <Spinner className={STAGE_ICON_CLASS_NAME} />;
    case "done":
      return <CheckIcon aria-hidden className={STAGE_ICON_CLASS_NAME} />;
    case "failed":
      return <XIcon aria-hidden className={STAGE_ICON_CLASS_NAME} />;
    case "warning":
      return <CircleAlertIcon aria-hidden className={STAGE_ICON_CLASS_NAME} />;
    case "skipped":
      return <MinusIcon aria-hidden className={STAGE_ICON_CLASS_NAME} />;
    case "pending":
      return <CircleIcon aria-hidden className={STAGE_ICON_CLASS_NAME} />;
  }
}

// Mirrors upstream t3code's stageRowClassName: a named lookup rather than a
// chained ternary, so a new status doesn't ratchet up this file's complexity.
function stageRowClassName(status: LaunchStage["status"]): string {
  switch (status) {
    case "failed":
      return "text-destructive-foreground";
    case "warning":
      return "text-warning-foreground";
    case "pending":
      return "text-secondary-label opacity-40";
    case "running":
    case "skipped":
    case "done":
      return "text-secondary-label";
  }
}

function stageTrailing(stage: LaunchStage): string | null {
  switch (stage.status) {
    case "pending":
      return null;
    case "skipped":
      return stage.detail ?? "skipped";
    case "running":
      return stage.percent !== null ? `${stage.percent}%` : stage.detail;
    case "done":
    case "warning":
    case "failed":
      return stage.detail;
  }
}

export function WorktreeLaunchStageRow({
  stage,
  now,
}: {
  readonly stage: LaunchStage;
  readonly now: number;
}) {
  const elapsed = launchElapsed(stage.startedAt, stage.endedAt, now);
  const color = stageRowClassName(stage.status);
  const trailing = stageTrailing(stage);
  return (
    <li
      className={`relative flex min-h-6 min-w-0 items-center gap-1.5 overflow-hidden rounded-md px-0.5 py-0.5 text-sm leading-relaxed ${color}`}
      data-launch-stage={stage.id}
      data-launch-status={stage.status}
    >
      <span className="flex size-6 shrink-0 items-center justify-center text-icon-muted">
        <StageIcon status={stage.status} />
      </span>
      <span className="min-w-0 flex-1 truncate">
        {stage.label}
        <span className="sr-only">, {stage.status}</span>
      </span>
      {trailing ? (
        <span className="min-w-0 truncate text-xs text-muted-foreground tabular-nums">
          {trailing}
        </span>
      ) : null}
      {elapsed && stage.status !== "skipped" && stage.status !== "pending" ? (
        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{elapsed}</span>
      ) : null}
    </li>
  );
}
